import React, { FC, useCallback, useMemo, useState } from 'react';
import { Button, Form, Input, Modal, Select, message } from 'antd';
import { connect } from 'react-redux';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { pickUpToolForSupplier } from '../../thunks/tools_thunks';
import { fieldTechsRestCrudThunksForSupplier } from '../../thunks/field_techs_thunks';
import ToolInfoHeader from './ToolInfoHeader';

const FORM_NAME = 'pick-up-tool-form';

const PickUpModal: FC<any> = ({
	visible,
	tool,
	onCancel,
	onSuccess,
	pickUpTool,
	fieldTechs,
	fetchFieldTechs,
	currentUser,
}): React.ReactElement => {
	const [saving, setSaving] = useState(false);

	const initialValues = useMemo(
		() => ({
			pickedUpFor: nullSafeGet('email', currentUser),
			notes: undefined,
		}),
		[currentUser]
	);

	const onSubmit = useCallback(
		(values) => {
			setSaving(true);
			pickUpTool(tool.id, { pickedUpFor: values.pickedUpFor, notes: values.notes })
				.then(() => {
					onSuccess();
				})
				.catch((err) => message.error(err))
				.finally(() => setSaving(false));
		},
		[tool, pickUpTool, onSuccess]
	);

	return (
		<Modal
			visible={visible}
			width={600}
			title="Pick Up"
			onCancel={onCancel}
			closable={true}
			footer={[
				<Button key="cancel" onClick={onCancel} size="large">
					Cancel
				</Button>,
				<Button
					type="primary"
					size="large"
					style={{ marginLeft: '16px' }}
					key="submit"
					htmlType="submit"
					form={FORM_NAME}
					loading={saving}
				>
					Pick Up
				</Button>,
			]}
		>
			<ToolInfoHeader tool={tool} />
			<Form
				id={FORM_NAME}
				layout="vertical"
				requiredMark={false}
				initialValues={initialValues}
				onFinish={onSubmit}
			>
				<Form.Item name="pickedUpFor" label="Pick Up for">
					<OWAsyncSelect
						stateSlice={fieldTechs}
						targetCollectionName="toolPickUpTechDropdown"
						valueAccessor={(el) => el.email}
						fetchData={(
							searchText,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) => {
							fetchFieldTechs(
								{ search: searchText ? searchText.trim() : '' },
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							);
						}}
						renderRecord={(fieldTech) => (
							<Select.Option key={fieldTech.email} value={fieldTech.email}>
								{nullSafeGet('supplierContact.nameGiven', fieldTech)}{' '}
								{nullSafeGet('supplierContact.nameFamily', fieldTech)} (
								{nullSafeGet('supplierContact.email', fieldTech)})
							</Select.Option>
						)}
						sortBy={{ sort_by: 'name', order: 'ascend' }}
						fetchMultiple={() => {}}
					/>
				</Form.Item>
				<Form.Item name="notes" label="Notes">
					<Input.TextArea placeholder="Add a note..." autoSize={{ minRows: 3 }} />
				</Form.Item>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	fieldTechs: state.field_techs,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
	pickUpTool: (toolId, data) => dispatch(pickUpToolForSupplier(toolId, data)),
	fetchFieldTechs: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			fieldTechsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(PickUpModal);
